import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { IContext } from './columns'
import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { ITodolistFlow } from '~/types/todolist'

import columns from './columns'
import SetTodolistModal from './SetTodolistModal'

import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useTodolistsListQuery } from '~/queries/todolists'
import { useUserDetailsQueries } from '~/queries/users'
import { transformByDeviceType } from '~/utils/application'

import './styles.scss'

const Todolists = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const tableRef = useRef<HTMLDivElement>(null)

  const gridRef = useRef<AgGridReact>(null)

  const todolistsManagementAuthorized = useIsUserAuthorized([AUTHORIZATIONS.VERSION.TODOLIST.WRITE])

  const [isSetToDoModalOpen, setIsSetToDoModalOpen] = useState(false)

  const [todolist, setSelectedTodolist] = useState<ITodolistFlow>()

  /** Queries & Mutations */

  const { appDetails, appKey, deviceType } = useAppSummary()

  const { data: todolists, isFetched: todolistsFetched } = useTodolistsListQuery({ appKey, deviceType })

  const createdByQueries =
    todolists?.filter(({ todolist }) => todolist.created_by).map(({ todolist }) => todolist.created_by) || []

  const updatedByQueries =
    todolists?.filter(({ todolist }) => todolist.updated_by).map(({ todolist }) => todolist.updated_by) || []

  const userQueries = useUserDetailsQueries([...createdByQueries, ...updatedByQueries])

  const userLoading = userQueries.some((e) => e.isLoading)

  const users: { user_id: string }[] | undefined = !userLoading
    ? userQueries.map(({ data }) => data || { user_id: '' })
    : undefined

  const rowData =
    todolistsFetched && users
      ? todolists?.map((todolist) => ({
          ...todolist,
          todolist: {
            ...todolist.todolist,
            created_by: users?.find((user) => user.user_id === todolist.todolist.created_by),
            updated_by: users?.find((user) => user.user_id === todolist.todolist.updated_by),
          },
        })) || []
      : undefined

  /** Effects */

  useDocumentTitle('To-do lists')

  /** Handlers */

  const handleEditTodo = (todolist: ITodolistFlow) => {
    setSelectedTodolist(todolist)
    setIsSetToDoModalOpen(true)
  }

  const handleRedirect = (path: string) => navigate(path)

  const handleRowClicked = (evt: RowClickedEvent<ITodolistFlow>) => {
    if (gridRef.current?.api.getFocusedCell()?.column.getColId() !== 'actions') {
      navigate(`/todolists/${evt.data?.todolist.id}`)
    }
  }

  const handleCreateTodoModal = (value: boolean) => () => setIsSetToDoModalOpen(value)

  const handleAddToDoSuccess = () => {
    setIsSetToDoModalOpen(false)
    setSelectedTodolist(undefined)
  }

  const handleAddToDoFailure = () => {
    setIsSetToDoModalOpen(false)
    setSelectedTodolist(undefined)
  }

  /** Render */

  const context: IContext = {
    application: `${appKey}:${deviceType}`,
    handleEditTodo,
    handleRedirect,
  }

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="todolists-view" withPadding>
        <ViewTitle className="layout-view-title">App to-do lists</ViewTitle>
        <ContentLayout>
          <TableWrapper
            addButton
            addButtonDisabled={!todolistsManagementAuthorized}
            addButtonLabel="New to-do list"
            addButtonTooltip={!todolistsManagementAuthorized ? 'unauthorized' : undefined}
            className="todolists-table with-min-height"
            columnDefs={columns}
            context={context}
            entriesName="to-do lists"
            onAddButtonClick={handleCreateTodoModal(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={rowData}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      <SetTodolistModal
        appKey={appKey}
        deviceType={deviceType}
        isOpen={isSetToDoModalOpen}
        onClose={handleCreateTodoModal(false)}
        onFailure={handleAddToDoFailure}
        onSuccess={handleAddToDoSuccess}
        todolistFlow={todolist}
      />
    </>
  )
}

export default Todolists
